import React, { useEffect, useState } from "react";
import { Plus, Save, X, Award } from "lucide-react";
import { CertificateTemplateDialog } from "@/components/CertificateTemplateDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function MassnahmenKatalog() {
  const [measures, setMeasures] = useState([]);
  const [edited, setEdited] = useState({});
  const [certModule, setCertModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/avgs/measures");
      setMeasures(r.data);
      const e = {};
      r.data.forEach((m) => { e[m.measure_id] = m.sections.map((s) => ({ ...s })); });
      setEdited(e);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateField = (measureId, idx, field, value) => {
    setEdited((prev) => {
      const sections = [...prev[measureId]];
      sections[idx] = { ...sections[idx], [field]: value };
      if (field === "price_per_ue" || field === "default_duration" || field === "default_vz_tz") {
        const mod = sections[idx].module_name || null;
        sections.forEach((s, i) => { if (i !== idx && (s.module_name || null) === mod) s[field] = value; });
      }
      return { ...prev, [measureId]: sections };
    });
  };

  const addSection = (measureId) => {
    setEdited((prev) => ({
      ...prev,
      [measureId]: [...prev[measureId], { section_id: null, module_name: "", name: "Neuer Abschnitt", default_ue: 0, price_per_ue: null, default_duration: "", default_vz_tz: "TZ" }],
    }));
  };

  const removeSection = (measureId, idx) => {
    setEdited((prev) => ({ ...prev, [measureId]: prev[measureId].filter((_, i) => i !== idx) }));
  };

  const save = async (measureId) => {
    setSavingId(measureId);
    try {
      await api.patch(`/avgs/measures/${measureId}`, { sections: edited[measureId] });
      toast.success("Maßnahme gespeichert");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingId(null); }
  };

  const createMeasure = async () => {
    if (!newName.trim()) { toast.error("Bitte Namen angeben"); return; }
    try {
      await api.post("/avgs/measures", { name: newName.trim(), sections: [] });
      toast.success("Maßnahme angelegt");
      setNewName(""); setNewOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (loading) return <div className="text-sm text-slate-500 text-center py-10">Lädt…</div>;

  return (
    <div data-testid="massnahmen-katalog">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-xl">
          Hinterlege je Modul den Preis pro Unterrichtseinheit (UE), Standard-Dauer und VZ/TZ. Die tatsächliche
          UE-Zahl (aus dem Gutschein des Kunden) gibt der Manager pro Kunde in der Akte ein — der Vertragspreis
          wird dann automatisch als UE × Preis/UE berechnet.
        </p>
        <button onClick={() => setNewOpen((v) => !v)} className="px-3 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5 shrink-0" data-testid="massnahme-new-btn">
          <Plus size={12} strokeWidth={1.5} /> Neue Maßnahme
        </button>
      </div>
      {newOpen && (
        <div className="flex gap-2 mb-6 items-end">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name der Maßnahme"
            className="input-tech text-xs" data-testid="massnahme-new-name-input" />
          <button onClick={createMeasure} className="px-3 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black" data-testid="massnahme-new-save-btn">Anlegen</button>
          <button onClick={() => setNewOpen(false)} className="px-3 py-2 text-xs font-medium border border-slate-300 hover:bg-slate-100">Abbrechen</button>
        </div>
      )}
      <div className="space-y-6">
        {measures.map((m) => (
          <div key={m.measure_id} className="border border-slate-200 bg-white" data-testid={`massnahme-card-${m.measure_id}`}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                <span className="text-sm font-medium text-slate-900">{m.name}</span>
                {!m.active && <span className="text-[10px] uppercase font-bold text-slate-400">archiviert</span>}
              </div>
              <button onClick={() => save(m.measure_id)} disabled={savingId === m.measure_id}
                className="px-3 py-1.5 text-xs font-medium bg-[#00CCC9] text-white hover:bg-[#00b3b0] disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`massnahme-save-${m.measure_id}`}>
                <Save size={12} strokeWidth={1.5} /> {savingId === m.measure_id ? "Speichert…" : "Speichern"}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-overline text-slate-500 text-left border-b border-slate-100">
                    <th className="px-4 py-2">Modul</th>
                    <th className="px-4 py-2">Abschnitt</th>
                    <th className="px-4 py-2 w-24">Standard-UE</th>
                    <th className="px-4 py-2 w-32">Preis/UE (€)</th>
                    <th className="px-4 py-2 w-28">Dauer</th>
                    <th className="px-4 py-2 w-20">VZ/TZ</th>
                    <th className="px-4 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {(edited[m.measure_id] || []).map((s, idx) => (
                    <tr key={s.section_id || `new-${idx}`} className="border-b border-slate-50">
                      <td className="px-4 py-2">
                        <input value={s.module_name || ""} onChange={(e) => updateField(m.measure_id, idx, "module_name", e.target.value)}
                          className="input-tech text-xs w-full" placeholder="— ohne Modul —" data-testid={`section-module-${m.measure_id}-${idx}`} />
                      </td>
                      <td className="px-4 py-2">
                        <input value={s.name || ""} onChange={(e) => updateField(m.measure_id, idx, "name", e.target.value)}
                          className="input-tech text-xs w-full" data-testid={`section-name-${m.measure_id}-${idx}`} />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" min="0" value={s.default_ue ?? 0}
                          onChange={(e) => updateField(m.measure_id, idx, "default_ue", parseFloat(e.target.value) || 0)}
                          className="input-tech font-mono text-xs w-20" data-testid={`section-ue-${m.measure_id}-${idx}`} />
                      </td>
                      <td className="px-4 py-2">
                        <input type="number" min="0" step="0.01" value={s.price_per_ue ?? ""}
                          onChange={(e) => updateField(m.measure_id, idx, "price_per_ue", e.target.value === "" ? null : parseFloat(e.target.value))}
                          placeholder="0,00" className="input-tech font-mono text-xs w-24" data-testid={`section-price-${m.measure_id}-${idx}`} />
                      </td>
                      <td className="px-4 py-2">
                        <input value={s.default_duration || ""} onChange={(e) => updateField(m.measure_id, idx, "default_duration", e.target.value)}
                          placeholder="z. B. 8 Wochen" className="input-tech text-xs w-24" data-testid={`section-duration-${m.measure_id}-${idx}`} />
                      </td>
                      <td className="px-4 py-2">
                        <select value={s.default_vz_tz || ""} onChange={(e) => updateField(m.measure_id, idx, "default_vz_tz", e.target.value)}
                          className="input-tech text-xs w-full" data-testid={`section-vztz-${m.measure_id}-${idx}`}>
                          <option value="">—</option>
                          <option value="VZ">VZ</option>
                          <option value="TZ">TZ</option>
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <button onClick={() => removeSection(m.measure_id, idx)} className="text-slate-400 hover:text-red-600" data-testid={`section-remove-${m.measure_id}-${idx}`}>
                          <X size={14} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2.5 border-t border-slate-100 flex flex-wrap items-center gap-2 text-xs" data-testid={`cert-modules-${m.measure_id}`}>
                <span className="inline-flex items-center gap-1 text-slate-500"><Award size={12} /> Zertifikat:</span>
                {[...new Set((m.sections || []).map((s) => s.module_name || s.name).filter(Boolean))].map((mod) => (
                  <button key={mod} onClick={() => setCertModule({ measureId: m.measure_id, module: mod })}
                    className="border border-slate-300 px-2 py-1 hover:bg-slate-900 hover:text-white transition-colors" data-testid={`cert-module-btn-${m.measure_id}-${mod}`}>{mod}</button>
                ))}
              </div>
            </div>
            <div className="px-4 py-3">
              <button onClick={() => addSection(m.measure_id)} className="text-xs font-medium text-[#0A6E6C] hover:underline flex items-center gap-1" data-testid={`section-add-${m.measure_id}`}>
                <Plus size={12} strokeWidth={1.5} /> Abschnitt hinzufügen
              </button>
            </div>
          </div>
        ))}
      </div>
      <CertificateTemplateDialog measureId={certModule?.measureId} module={certModule?.module} onClose={() => setCertModule(null)} />
    </div>
  );
}
